import React, { useEffect, useState } from "react";
import { MenuListComponentProps, OptionTypeBase } from "react-select";
import { List, RowComponentProps, useListRef } from "react-window";

const ITEM_HEIGHT = 40;

interface OptionRowProps {
  childrenArray: React.ReactNode[];
}

function OptionRow({
  ariaAttributes,
  index,
  style,
  childrenArray,
}: RowComponentProps<OptionRowProps>): JSX.Element {
  return (
    <div className="option-wrapper" style={style} {...ariaAttributes}>
      {childrenArray[index]}
    </div>
  );
}

const OptimizedMenuList: React.FC<MenuListComponentProps<OptionTypeBase, any>> = props => {
  const { options, children, maxHeight, getValue } = props;
  const listRef = useListRef(null);

  const selectedValues = getValue();
  const childrenArray = Array.isArray(children) ? children : [];
  const itemCount = childrenArray.length;

  // Computed once on mount, matching the previous initialScrollOffset (mount-only) behavior
  const [initialScrollIndex] = useState(() => {
    // Calculate how many items can fit in the visible viewport
    const visibleItemsCount = Math.floor(maxHeight / ITEM_HEIGHT);

    // Only scroll when the list is longer than the visible area, this prevents hiding elements in small lists
    // For larger lists, it centers the selected item in the viewport for better UX
    if (itemCount > visibleItemsCount && selectedValues && selectedValues[0]) {
      const selectedIndex = options.indexOf(selectedValues[0]);
      if (selectedIndex > 0) {
        return selectedIndex;
      }
    }
    return null;
  });

  useEffect(() => {
    if (initialScrollIndex !== null) {
      listRef.current?.scrollToRow({ index: initialScrollIndex, align: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <List
      listRef={listRef}
      style={{ height: maxHeight }}
      rowComponent={OptionRow}
      rowCount={itemCount}
      rowHeight={ITEM_HEIGHT}
      rowProps={{ childrenArray }}
    />
  );
};

export default OptimizedMenuList;
